import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", beta_code: "" });
  const [loading, setLoading] = useState(false);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (!form.beta_code.trim()) {
      toast.error("Beta access code required — request one on the landing page");
      return;
    }
    setLoading(true);
    try {
      await register(form.email, form.password, form.name, form.beta_code.trim());
      toast.success("Account created — welcome to VitaQMS");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-slate-900 rounded flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-extrabold text-xl">VitaQMS</span>
          </Link>

          <div className="vq-overline mb-3">Early access — beta</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900 mb-2">
            Create your workspace.
          </h1>
          <p className="text-slate-600 mb-8">
            VitaQMS is invite-only during our beta. Enter your access code below, or{" "}
            <Link to="/" className="font-semibold text-slate-900 hover:text-[#0055FF]">
              request one here
            </Link>.
          </p>

          <Button
            data-testid="google-signup-btn"
            onClick={handleGoogle}
            variant="outline"
            className="w-full h-11 border-slate-300 font-medium mb-6"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09 0-.73.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative my-6 flex items-center">
            <div className="flex-1 border-t border-slate-200" />
            <span className="px-3 text-xs font-mono text-slate-400">OR EMAIL</span>
            <div className="flex-1 border-t border-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-sm font-semibold text-slate-700">Full name</Label>
              <Input
                data-testid="register-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 h-11 bg-white"
                placeholder="Dr. Elena Voss"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700">Work email</Label>
              <Input
                data-testid="register-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 h-11 bg-white"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700">Password</Label>
              <Input
                data-testid="register-password-input"
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 h-11 bg-white"
                placeholder="Minimum 8 characters"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700">Beta access code</Label>
              <Input
                data-testid="register-beta-code-input"
                required
                value={form.beta_code}
                onChange={(e) => setForm({ ...form, beta_code: e.target.value.toUpperCase() })}
                className="mt-1.5 h-11 bg-white font-mono tracking-wider"
                placeholder="VITAQMS-XXXXXXXX"
              />
              <p className="text-[10px] font-mono text-slate-500 mt-1">
                Codes are issued by your VitaQMS contact. No code? Request one on the landing page.
              </p>
            </div>
            <Button
              data-testid="register-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-slate-900 hover:bg-[#0055FF] text-white font-semibold rounded-md"
            >
              {loading ? "Creating…" : "Create workspace"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-600 text-center">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-slate-900 hover:text-[#0055FF]">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-white relative overflow-hidden order-1 lg:order-2">
        <div className="absolute inset-0 vq-dot-bg opacity-30" />
        <div className="relative">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white rounded flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-slate-900" />
            </div>
            <span className="font-display font-extrabold text-xl">VitaQMS</span>
          </Link>
        </div>
        <div className="relative">
          <div className="vq-overline text-slate-400 mb-3">What beta testers get</div>
          <ul className="space-y-3 font-medium">
            <li>✓ Unified QMS + clinical trial platform (world&apos;s first)</li>
            <li>✓ ISO 13485 · MDR/IVDR · FDA QMSR · MDSAP · AI Act — one workspace</li>
            <li>✓ Live clinical evidence feeds into design + risk file</li>
            <li>✓ Patient advisory &amp; co-design built in</li>
            <li>✓ Direct line to the founding team</li>
          </ul>
        </div>
        <div className="relative text-xs font-mono text-slate-400">
          © VitaQMS.com • by PerCen360
        </div>
      </div>
    </div>
  );
}
